import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinChecking, setPinChecking] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  const handleAdminClick = (e) => {
    e.preventDefault();
    setShowPinModal(true);
    setPin("");
    setPinError("");
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pinChecking) return;
    setPinChecking(true);
    setPinError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/settings/verify-admin-pin`, { pin });
      if (data?.valid) {
        setShowPinModal(false);
        setPin("");
        navigate("/login");
      } else {
        setPinError("Code incorrect");
        setPin("");
      }
    } catch (err) {
      setPinError("Erreur de vérification. Réessayez.");
    } finally {
      setPinChecking(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const query = search.trim();
    if (query) {
      navigate(`/produits?search=${encodeURIComponent(query)}`);
    } else {
      navigate('/produits');
    }
    setIsMenuOpen(false);
  };

  const clearSearch = () => {
    setSearch('');
    navigate('/produits');
    setIsMenuOpen(false);
  };

  const navLinks = [
    { to: "/", label: "Accueil" },
    { to: "/domoelec", label: "DOMOELEC" },
    { to: "/produits", label: "Produits" },
    { to: "/services", label: "Services" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      {/* === TOP BAR === */}
      <div className="hdr-topbar">
        <div className="hdr-topbar-inner">
          <div className="hdr-topbar-left">
            <a href="tel:0522340659" className="hdr-topbar-item">
              <i className="fas fa-phone-alt"></i>
              <span>05 22 34 06 59</span>
            </a>
            <span className="hdr-topbar-sep">|</span>
            <a href="mailto:contact@domoelec.com" className="hdr-topbar-item">
              <i className="fas fa-envelope"></i>
              <span>contact@domoelec.com</span>
            </a>
          </div>
          <div className="hdr-topbar-right">
            <a href="https://www.linkedin.com/company/domoelecsarl/" target="_blank" rel="noopener noreferrer" className="hdr-topbar-social">
              <i className="fab fa-linkedin-in"></i>
            </a>
            <span className="hdr-topbar-sep">|</span>
            <button onClick={handleAdminClick} className="hdr-topbar-admin">
              <i className="fas fa-user-shield"></i>
              <span>Espace Admin</span>
            </button>
          </div>
        </div>
      </div>

      {/* === MAIN HEADER === */}
      <header className={`hdr-main ${scrolled ? 'hdr-scrolled' : ''}`}>
        {/* Middle row: Logo + Search + CTA (desktop) / Logo centered + hamburger (mobile) */}
        <div className="hdr-middle">
          <div className="hdr-middle-inner">
            <Link to="/" className="hdr-brand">
              <img src="/images/logofav.png" alt="DOMOELEC" className="hdr-brand-img" />
              <span className="hdr-brand-name"><span className="hdr-green">D</span>OMO<span className="hdr-green">E</span>LEC</span>
            </Link>

            {/* Search - visible dans le middle row sur desktop, passe en row séparée sur mobile */}
            <form className="hdr-search" onSubmit={onSearchSubmit} role="search">
              <i className="fas fa-search hdr-search-icon"></i>
              <input
                type="search"
                placeholder="Rechercher un produit, une référence..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Rechercher un produit ou une référence"
              />
              {search && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearSearch(); }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="hdr-search-clear"
                  aria-label="Effacer"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              <button type="submit" className="hdr-search-btn">
                <i className="fas fa-search hdr-search-btn-icon"></i>
                <span>Rechercher</span>
              </button>
            </form>

            {/* CTA - desktop only */}
            <div className="hdr-cta-group">
              <Link to="/contact" className="hdr-cta">
                <i className="far fa-paper-plane"></i>
                <div>
                  <small>Demander un</small>
                  <strong>Devis Gratuit</strong>
                </div>
              </Link>
              <a href="tel:0522340659" className="hdr-cta hdr-cta-phone">
                <i className="fas fa-phone-volume"></i>
                <div>
                  <small>Appelez-nous</small>
                  <strong>05 22 34 06 59</strong>
                </div>
              </a>
            </div>

            {/* Hamburger - mobile only */}
            <button className={`hdr-hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line className="hdr-ham-top" x1="3" y1="6" x2="21" y2="6" />
                <line className="hdr-ham-mid" x1="3" y1="12" x2="21" y2="12" />
                <line className="hdr-ham-bot" x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop Navigation Bar */}
        <nav className="hdr-nav-desktop">
          <div className="hdr-nav-inner">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`hdr-nav-link ${location.pathname === link.to ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Mobile Search Bar (bottom of header, mobile only) */}
        <div className="hdr-search-mobile">
          <form className="hdr-search-m" onSubmit={onSearchSubmit} role="search">
            <i className="fas fa-search"></i>
            <input
              type="search"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher un produit"
            />
            {search && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearSearch(); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="hdr-search-clear"
                aria-label="Effacer"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
            <button type="submit" className="hdr-search-m-btn">
              <i className="fas fa-search"></i>
            </button>
          </form>
        </div>
      </header>

      {/* === MOBILE SIDEBAR === */}
      <div className={`hdr-mobile-sidebar ${isMenuOpen ? 'open' : ''}`}>
        <div className="hdr-mobile-sidebar-header">
          <span className="hdr-mobile-sidebar-title">
            <span className="hdr-green">D</span>OMO<span className="hdr-green">E</span>LEC
          </span>
          <button className="hdr-mobile-sidebar-close" onClick={() => setIsMenuOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <nav className="hdr-mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`hdr-mobile-link ${location.pathname === link.to ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="hdr-mobile-footer">
          <a href="tel:0522340659" className="hdr-mobile-contact">
            <i className="fas fa-phone-alt"></i>
            <span>05 22 34 06 59</span>
          </a>
          <a href="mailto:contact@domoelec.com" className="hdr-mobile-contact">
            <i className="fas fa-envelope"></i>
            <span>contact@domoelec.com</span>
          </a>
        </div>
      </div>
      {isMenuOpen && <div className="hdr-overlay" onClick={() => setIsMenuOpen(false)}></div>}

      {/* PIN Modal */}
      {showPinModal && (
        <div className="hdr-modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="hdr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hdr-modal-icon">
              <i className="fas fa-lock"></i>
            </div>
            <h3>Espace Admin</h3>
            <p>Saisissez le code PIN pour continuer</p>
            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                value={pin}
                onChange={(e) => { setPin(e.target.value); setPinError(""); }}
                placeholder="----"
                maxLength={6}
                autoFocus
                className={`hdr-modal-input ${pinError ? 'error' : ''}`}
              />
              {pinError && <p className="hdr-modal-error">{pinError}</p>}
              <button type="submit" className="hdr-modal-submit" disabled={pinChecking || !pin}>
                {pinChecking ? 'Vérification…' : 'Valider'}
              </button>
            </form>
            <button onClick={() => setShowPinModal(false)} className="hdr-modal-cancel">Annuler</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
